import { CheckCircleFilled } from "@ant-design/icons"
import React, { useId, useRef } from "react"
import { useTranslation } from "react-i18next"

import { cn } from "../../../utils/cn.ts"
import type { AutomationOptions, Channel } from "../types/automation.types"

interface ChannelChoiceProps {
  value?: Channel
  channels: AutomationOptions["channels"]
  onChange?: (channel: Channel) => void
}

const NEXT_KEYS = ["ArrowRight", "ArrowDown"]
const PREVIOUS_KEYS = ["ArrowLeft", "ArrowUp"]

function ChannelChoice({ value, channels, onChange }: ChannelChoiceProps) {
  const { t } = useTranslation()
  const idPrefix = useId()
  const cardRefs = useRef<(HTMLDivElement | null)[]>([])
  const available = channels.filter((channel) => channel.status === "available")
  // Roving tabindex: the checked card is the group's single Tab stop (or the
  // first selectable one if nothing selectable is checked).
  const tabStop =
    available.find((channel) => channel.key === value)?.key ?? available[0]?.key

  const select = (channel: AutomationOptions["channels"][number]) => {
    if (channel.status !== "available" || channel.key === value) return
    onChange?.(channel.key)
  }

  const moveSelection = (from: number, step: 1 | -1) => {
    for (let offset = 1; offset < channels.length; offset += 1) {
      const index = (from + step * offset + channels.length) % channels.length
      if (channels[index].status === "available") {
        cardRefs.current[index]?.focus()
        select(channels[index])
        return
      }
    }
  }

  return (
    <div
      role={"radiogroup"}
      aria-label={t("settingsAutomation.builder.basics.channelLabel")}
      className={"grid grid-cols-1 gap-3 sm:grid-cols-2"}
    >
      {channels.map((channel, index) => {
        const disabled = channel.status !== "available"
        const checked = !disabled && channel.key === value
        const labelId = `${idPrefix}-${channel.key}-label`
        const descriptionId = `${idPrefix}-${channel.key}-description`
        return (
          <div
            key={channel.key}
            ref={(node) => {
              cardRefs.current[index] = node
            }}
            role={"radio"}
            aria-checked={checked}
            aria-disabled={disabled ? "true" : undefined}
            aria-labelledby={labelId}
            aria-describedby={disabled ? descriptionId : undefined}
            tabIndex={channel.key === tabStop ? 0 : -1}
            onClick={() => select(channel)}
            onKeyDown={(event) => {
              if (event.key === " " || event.key === "Enter") {
                event.preventDefault()
                select(channel)
              } else if (NEXT_KEYS.includes(event.key)) {
                event.preventDefault()
                moveSelection(index, 1)
              } else if (PREVIOUS_KEYS.includes(event.key)) {
                event.preventDefault()
                moveSelection(index, -1)
              }
            }}
            className={cn(
              "flex min-h-12 items-center justify-between gap-2 rounded-lg border px-4 py-3",
              "outline-none transition-colors motion-reduce:transition-none",
              "focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2",
              // A 1px border plus a 1px ring reads as a 2px selected outline
              // without shifting the card's content.
              checked && "border-primary bg-white ring-1 ring-primary",
              !checked && !disabled && "cursor-pointer border-gray-300 bg-white hover:border-primary",
              disabled && "cursor-not-allowed border-gray-200 bg-gray-50 text-gray-600"
            )}
          >
            <span id={labelId} className={"font-medium"}>
              {channel.label}
            </span>
            {checked && (
              <CheckCircleFilled aria-hidden={"true"} className={"text-base text-primary"} />
            )}
            {disabled && (
              <span
                id={descriptionId}
                className={"rounded-full bg-gray-200 px-2 py-0.5 text-xs font-medium text-gray-700"}
              >
                {/* Only an unavailable channel carries a status; the checked one shows a check. */}
                {t("settingsAutomation.builder.basics.channelComingSoon")}
              </span>
            )}
          </div>
        )
      })}
    </div>
  )
}

export { type ChannelChoiceProps, ChannelChoice }
